import { useCallback, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useRouteByName } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { dashboardConfigAndWRMResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import {
	generatePath,
	useResourceStoreContext,
	useRouterActions,
} from '@atlassian/react-resource-router';

export const useCreateDashboardGadgetModalControls = () => {
	const [isOpen, setOpen] = useState(false);
	const route = useRouteByName('dashboard-edit');
	const { path: dashboardPath } = route ?? {};
	const [_, { clearResource }] = useResourceStoreContext();
	const { push } = useRouterActions();

	const clearAndNavigateToDashboard = useCallback(
		(dashboardId: string) => {
			const url = generatePath(dashboardPath, { dashboardId });
			route && dashboardPath
				? clearResource(dashboardConfigAndWRMResource, {
						route,
						match: {
							params: { dashboardId },
							path: dashboardPath,
							// NOTE - these are not used in dashboardConfigAndWRMResource's getKey but the types require them
							query: {},
							isExact: true,
							url,
						},
						query: {},
					})
				: null;
			setOpen(false);
			push(url);
		},
		[clearResource, dashboardPath, push, route],
	);

	const closeModal = useCallback(() => {
		setOpen(false);
	}, []);

	const openModal = useCallback(() => {
		setOpen(true);
	}, []);

	return { isOpen, clearAndNavigateToDashboard, closeModal, openModal };
};

export const getCreateDashboardGadgetModalParams = (
	jql: string,
	filterId: string | undefined,
	isCustomFilter: (filterId: string) => boolean,
	hasCustomFilterJqlBeenModified: boolean,
) => {
	if (jql == null && filterId == null) {
		fireErrorAnalytics({
			meta: {
				id: 'getCreateDashboardGadgetModalParams',
				packageName: 'jiraIssueNavigatorActionExportIssues',
				teamName: 'empanada',
			},
			error: new Error('jql and filter are both null'),
		});
	}

	return filterId != null && isCustomFilter(filterId) && !hasCustomFilterJqlBeenModified
		? { jql: undefined, filterId }
		: { filterId: undefined, jql };
};

/**
 * Appends a predefined policy to an existing rel policy to prevent adding a potentially
 * oversized JQL string URL parameter to request headers.
 *
 * At the moment, we are sending a referrer link as a request header that contains
 * the full JQL string of the request (i.e. {TENANT_URL}/issues?jql={LONG_JQL_STRING}).
 * This referrer link is redundant and interferes with the JQL character lengths
 * that issues export supports (tracked as an issue by JRACLOUD-77201).
 *
 * Specifically, at a JQL character length of ~2150 to ~2634 it returns a 400 (Bad Request)
 * error with reason of java.lang.IllegalArgumentException: "Request header is too large",
 * which is a character length below what is expected behaviour.
 *
 * The function splits the existing policy into individual strings, filters out any
 * duplicate or empty strings, and then concatenates the predefined export issues
 * policy to the remaining unique strings. The function is case insensitive.
 *
 * @param {string} existingRelPolicy - The existing rel policy.
 * @returns {string} - The updated rel policy.
 */
export const appendExportIssuesRelPolicy = (existingRelPolicy: string): string => {
	const EXPORT_ISSUES_REL_POLICY = ['noopener', 'noreferrer', 'nofollow'];

	return existingRelPolicy
		.toLowerCase()
		.split(' ')
		.map((string) => string.trim())
		.filter((string) => string && !EXPORT_ISSUES_REL_POLICY.includes(string))
		.concat(EXPORT_ISSUES_REL_POLICY)
		.join(' ');
};

export const is4xxError = (statusCode?: number | null) => {
	return statusCode && statusCode >= 400 && statusCode < 500;
};

export const getStatusCodeGroup = (statusCode?: number | null): string => {
	if (statusCode) {
		if (statusCode >= 100 && statusCode < 200) return '1xx';
		if (statusCode >= 300 && statusCode < 400) return '3xx';
		if (statusCode >= 400 && statusCode < 500) return '4xx';
		if (statusCode >= 500 && statusCode < 600) return '5xx';
	}

	return 'unknown';
};
